/**
 * BackgroundJobsPanel — Floating panel that tracks jobs initiated by the user.
 *
 * Only shows jobs added via the store's addJob() (from import/evaluate buttons).
 * Polls individual job status every 3s while active.
 *
 * A template import fans out into 1 import job + N section-extraction jobs;
 * rendering them all buried the panel in cards (Carlos, 21/08). Jobs that
 * prepare the SAME template are therefore rendered as ONE aggregate card with
 * a single "view template" link. Completed cards auto-dismiss 60s after
 * completion (or immediately when their link is followed); failed cards stay
 * until dismissed.
 *
 * Pattern: same as optim-evaluator's EvaluationJobsPanel.
 */

import React, { useCallback, useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router"
import { useTranslations } from "@/hooks/useTranslations"
import {
	ChevronDown,
	ChevronUp,
	X,
	Loader2,
	CheckCircle2,
	XCircle,
	Ban,
	Clock,
	ExternalLink,
	RotateCcw,
	Trash2,
} from "lucide-react"
import { isSilentJob, useBackgroundJobsStore, type BackgroundJob, type JobStatus, type JobType } from "@/stores/backgroundJobsStore"
import { Progress } from "@/components/ui/progress"
import { Button } from "@/components/ui/button"
import { getAuthHeaders } from "@/lib/apiUtils"
import { resolveI18nString } from "@/lib/locale-format"

const POLL_INTERVAL_MS = 3000
// Completed cards dismiss themselves this long after completion (failed cards stay).
const AUTO_DISMISS_MS = 60_000

// ── Status helpers ──────────────────────────────────────────────────

function StatusIcon({ status }: { status: JobStatus }) {
	switch (status) {
		case "pending":
			return <Clock className="h-4 w-4 text-muted-foreground" />
		case "processing":
			return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
		case "completed":
			return <CheckCircle2 className="h-4 w-4 text-green-500" />
		case "failed":
			return <XCircle className="h-4 w-4 text-red-500" />
		case "cancelled":
			return <Ban className="h-4 w-4 text-muted-foreground" />
		default:
			return null
	}
}

function statusLabel(status: JobStatus, t: ReturnType<typeof useTranslations>): string {
	switch (status) {
		case "pending": return t("backgroundJobs.status.pending")
		case "processing": return t("backgroundJobs.status.processing")
		case "completed": return t("backgroundJobs.status.completed")
		case "failed": return t("backgroundJobs.status.failed")
		case "cancelled": return t("backgroundJobs.status.cancelled")
		default: return status
	}
}

function jobTypeLabel(type: string, t: ReturnType<typeof useTranslations>): string {
	switch (type) {
		case "tender_import": return t("backgroundJobs.import")
		case "evaluation": return t("backgroundJobs.evaluation")
		case "comparison": return t("backgroundJobs.comparison")
		case "pliego_generation": return t("backgroundJobs.pliegoGeneration")
		case "section_generation": return t("backgroundJobs.sectionGeneration")
		case "section_extraction": return t("backgroundJobs.sectionExtraction")
		case "specs_analysis": return t("backgroundJobs.specsAnalysis")
		case "template_decision_analysis": return t("backgroundJobs.templateDecisionAnalysis")
		case "document_review": return t("backgroundJobs.documentReview")
		case "document_ocr": return t("backgroundJobs.documentOcr")
		case "report_chat_execute": return t("backgroundJobs.reportChatExecute")
		case "criteria_extraction": return t("backgroundJobs.criteriaExtraction")
		case "criteria_enrichment": return t("backgroundJobs.criteriaEnrichment")
		case "objective_suggest": return t("backgroundJobs.objectiveSuggest")
		default: return type
	}
}

// ── Job row ─────────────────────────────────────────────────────────

/** Wizard route that can resume a specs-analysis job (?job=<id>). */
function specsAnalysisResumeLink(job: BackgroundJob): string {
	const input = job.input as Record<string, unknown> | null
	const base = input?.resumeMode === "template"
		? "/procurement-templates/import"
		: input?.resumeMode === "import"
			? "/tenders/import"
			: "/evaluations/new/from-documents"
	return `${base}?job=${job.id}`
}

/** Get a link for a completed job (navigates to result) */
function getJobLink(job: BackgroundJob): string | null {
	// Specs analysis: consumed → the created entity; otherwise resume the wizard
	// at the review step.
	if (job.type === "specs_analysis" && job.status === "completed") {
		const result = job.result as Record<string, unknown> | null
		if (result?.createdExpedientId) return `/expedients/${result.createdExpedientId}`
		if (result?.createdTemplateId) return `/procurement-templates/${result.createdTemplateId}`
		return specsAnalysisResumeLink(job)
	}
	// Document review: link back to the editor and auto-open the review panel.
	if (job.type === "document_review") {
		if (job.status !== "completed") return null
		const input = job.input as Record<string, unknown> | null
		const expedientId = input?.expedientId as string | undefined
		return expedientId ? `/expedients/${expedientId}/editor?review=open` : null
	}

	// Section extraction: link directly to the document editor
	if (job.type === "section_extraction" && job.status === "completed") {
		const input = job.input as Record<string, unknown> | null
		const result = job.result as Record<string, unknown> | null
		// Find the reportId from the first extracted document
		const docs = (result?.documents as Array<Record<string, unknown>>) || []
		const reportId = docs[0]?.reportId as string | undefined

		if (input?.targetType === "template" && input?.targetId) {
			return reportId
				? `/procurement-templates/${input.targetId}/editor?doc=${reportId}`
				: `/procurement-templates/${input.targetId}`
		}
		if (input?.targetType === "expedient" && input?.targetId) {
			return reportId
				? `/expedients/${input.targetId}/editor?doc=${reportId}`
				: `/expedients/${input.targetId}`
		}
		return null
	}

	// Model decision analysis: link to the template detail, where the decisions are shown.
	if (job.type === "template_decision_analysis" && job.status === "completed") {
		const input = job.input as Record<string, unknown> | null
		return input?.templateId ? `/procurement-templates/${input.templateId}` : null
	}

	// Pliego generation: completed → the expedient EDITOR (the expedient page
	// has no editor affordance, so landing there dead-ended the user);
	// active → tender-assistant session.
	if (job.type === "pliego_generation") {
		const result = job.result as Record<string, unknown> | null
		if (job.status === "completed" && result?.expedientId) {
			return `/expedients/${result.expedientId}/editor`
		}
		return null
	}

	// Section regeneration: back to the editor once the new text is in.
	if (job.type === "section_generation") {
		const expedientId = (job.input as { expedientId?: string } | null)?.expedientId
		return job.status === "completed" && expedientId ? `/expedients/${expedientId}/editor` : null
	}

	// Criteria re-extraction (publish): the refreshed criteria live in the
	// expedient's evaluation tab.
	if (job.type === "criteria_extraction") {
		const expedientId = (job.input as { expedientId?: string } | null)?.expedientId
		return job.status === "completed" && expedientId ? `/expedients/${expedientId}?tab=evaluation` : null
	}

	// Other job types: only show link on completion
	if (job.status !== "completed" || !job.result) return null
	const result = job.result as Record<string, unknown>

	if (job.type === "tender_import" && result.results) {
		const entries = Object.values(result.results as Record<string, { templateId?: string; expedientId?: string; success: boolean }>)
		// Prefer template link (new flow), fall back to expedient (legacy imports)
		const firstTemplate = entries.find((e) => e.success && e.templateId)
		if (firstTemplate?.templateId) return `/procurement-templates/${firstTemplate.templateId}`
		const firstExpedient = entries.find((e) => e.success && e.expedientId)
		return firstExpedient?.expedientId ? `/expedients/${firstExpedient.expedientId}` : null
	}

	if (job.type === "evaluation" && result.expedientId) {
		const highlight = result.proposalId
			? `proposal-${result.proposalId}`
			: result.lotId
				? `lot-${result.lotId}`
				: null
		return `/expedients/${result.expedientId}?tab=evaluation${highlight ? `&highlight=${highlight}` : ""}`
	}
	// Old jobs stored no expedientId: no link beats a link to the expedient list,
	// which reads as "the button is broken" (BUG-057).

	if (job.type === "comparison" && result.expedientId) {
		const lotId = result.lotId as string | null
		const highlight = lotId ? `comparison-${lotId}` : "comparison-general"
		return `/expedients/${result.expedientId}?tab=evaluation&highlight=${highlight}`
	}

	return null
}

/**
 * Label the button after its DESTINATION, never after the job type (BUG-029).
 * These links have fallbacks: "Veure document" landed on the template detail page
 * when the extraction returned no reportId, and "Veure plantilla" landed on an
 * expedient for legacy imports. The button said one thing and did another.
 */
function labelKeyForLink(link: string): string {
	if (link.includes("/editor")) return "backgroundJobs.viewDocument"
	if (link.startsWith("/procurement-templates/")) return "backgroundJobs.viewTemplate"
	return "backgroundJobs.viewExpedient"
}

// ── Template-preparation aggregation ────────────────────────────────
//
// Importing one tender spawns 1 tender_import job + one section_extraction job
// per document type, all pointing at the same procurement template. Shown
// individually they flooded the panel with 6-7 cards, each with its own
// button. They are folded into ONE card per template instead.

interface TemplateGroup {
	targetId: string
	/** Template/tender display name (from the extraction jobs' entityTitle). */
	entityTitle: string
	/** All member job ids (import + extractions) — dismissed/cleared together. */
	memberIds: string[]
	/** Members still pending/processing (cancel targets). */
	activeMemberIds: string[]
	status: "processing" | "completed" | "failed"
	/** Extraction jobs done / known. */
	completedDocs: number
	totalDocs: number
	/** Mean progress across members, 0-100. */
	progress: number
	/** First failed/cancelled member (extraction preferred), for the error card. */
	failedMember: BackgroundJob | null
	/** Latest member completion time — only set once the whole group is done. */
	completedAt: string | null
}

type DisplayItem =
	| { kind: "job"; job: BackgroundJob }
	| { kind: "group"; group: TemplateGroup }

/** Template ids a tender_import job has produced so far (result is written progressively). */
function importTemplateIds(job: BackgroundJob): string[] {
	const result = job.result as { results?: Record<string, { templateId?: string }> } | null
	if (!result?.results) return []
	const ids: string[] = []
	for (const entry of Object.values(result.results)) {
		if (entry?.templateId && !ids.includes(entry.templateId)) ids.push(entry.templateId)
	}
	return ids
}

/**
 * Fold every template-targeted section_extraction (plus the single-template
 * tender_import that produced it) into one TemplateGroup; everything else
 * keeps its individual card. A bulk import that created SEVERAL templates
 * keeps its own card — it cannot belong to one group.
 */
function buildDisplayItems(jobs: BackgroundJob[]): DisplayItem[] {
	const extractionsByTemplate = new Map<string, BackgroundJob[]>()
	for (const job of jobs) {
		if (job.type !== "section_extraction") continue
		const input = job.input as Record<string, unknown> | null
		if (input?.targetType !== "template" || typeof input?.targetId !== "string") continue
		const list = extractionsByTemplate.get(input.targetId) || []
		list.push(job)
		extractionsByTemplate.set(input.targetId, list)
	}
	if (extractionsByTemplate.size === 0) return jobs.map((job) => ({ kind: "job", job }))

	const importByTemplate = new Map<string, BackgroundJob>()
	for (const job of jobs) {
		if (job.type !== "tender_import") continue
		const ids = importTemplateIds(job)
		if (ids.length === 1 && extractionsByTemplate.has(ids[0]) && !importByTemplate.has(ids[0])) {
			importByTemplate.set(ids[0], job)
		}
	}

	const groups = new Map<string, TemplateGroup>()
	for (const [targetId, extractions] of extractionsByTemplate) {
		const importJob = importByTemplate.get(targetId)
		const members = importJob ? [importJob, ...extractions] : extractions
		const isTerminalFailure = (j: BackgroundJob) => j.status === "failed" || j.status === "cancelled"
		const failedMember = extractions.find(isTerminalFailure) ?? members.find(isTerminalFailure) ?? null
		const hasActive = members.some((j) => j.status === "pending" || j.status === "processing")
		const status: TemplateGroup["status"] = failedMember ? "failed" : hasActive ? "processing" : "completed"
		const entityTitle = (extractions
			.map((j) => (j.input as Record<string, unknown> | null)?.entityTitle)
			.find((title): title is string => typeof title === "string" && title.length > 0)
			?? extractions[0]?.title
			?? "")
			// Extraction jobs prefix their entityTitle with the document type
			// ("PCAP — Reurbanización de calles…"); the aggregate card names the
			// TEMPLATE, so the per-document prefix is noise here.
			.replace(/^(PCAP|PPT|IJ|MENOR|MINOR|Informe Justificatiu|Informe Justificativo)\s*[—–:·-]\s*/i, "")
		groups.set(targetId, {
			targetId,
			entityTitle,
			memberIds: members.map((j) => j.id),
			activeMemberIds: members.filter((j) => j.status === "pending" || j.status === "processing").map((j) => j.id),
			status,
			completedDocs: extractions.filter((j) => j.status === "completed").length,
			totalDocs: extractions.length,
			progress: Math.round(
				members.reduce((sum, j) => sum + (j.status === "completed" ? 100 : j.progress || 0), 0) / members.length,
			),
			failedMember,
			completedAt: status === "completed"
				? members.reduce<string | null>(
					(latest, j) => (j.completedAt && (!latest || j.completedAt > latest) ? j.completedAt : latest),
					null,
				)
				: null,
		})
	}

	// Emit each group at the position of its newest member (jobs are newest-first).
	const memberToGroup = new Map<string, string>()
	for (const group of groups.values()) {
		for (const id of group.memberIds) memberToGroup.set(id, group.targetId)
	}
	const items: DisplayItem[] = []
	const emitted = new Set<string>()
	for (const job of jobs) {
		const groupId = memberToGroup.get(job.id)
		if (!groupId) {
			items.push({ kind: "job", job })
		} else if (!emitted.has(groupId)) {
			emitted.add(groupId)
			items.push({ kind: "group", group: groups.get(groupId)! })
		}
	}
	return items
}

/** Completed less than AUTO_DISMISS_MS ago (missing timestamp = treat as fresh). */
function isFreshCompletion(completedAt: string | null): boolean {
	if (!completedAt) return true
	return Date.now() - new Date(completedAt).getTime() < AUTO_DISMISS_MS
}

/**
 * A completed card whose result link the user may not have seen yet (BUG-070).
 * Cards older than AUTO_DISMISS_MS no longer count: they are about to dismiss
 * themselves, so they must not keep the panel expanded forever.
 */
function hasFreshCompletedNotice(items: DisplayItem[]): boolean {
	return items.some((item) =>
		item.kind === "group"
			? item.group.status === "completed" && isFreshCompletion(item.group.completedAt)
			: item.job.status === "completed" && getJobLink(item.job) !== null && isFreshCompletion(item.job.completedAt),
	)
}

function JobRow({ job, onCancel, onDismiss, onNavigate, onRetry, isRetrying, router, t }: {
	job: BackgroundJob
	onCancel: (id: string) => void
	onDismiss: (id: string) => void
	onNavigate: (path: string, jobIds: string[]) => void
	onRetry: (id: string) => void
	isRetrying: boolean
	router: ReturnType<typeof useNavigate>
	t: ReturnType<typeof useTranslations>
}) {
	const isActive = job.status === "pending" || job.status === "processing"
	const isTerminal = job.status === "completed" || job.status === "failed" || job.status === "cancelled"
	const RETRYABLE_TYPES = ["tender_import", "evaluation", "comparison", "section_extraction", "pliego_generation"]
	const canRetry = (job.status === "failed" || job.status === "cancelled") && RETRYABLE_TYPES.includes(job.type)
	const link = getJobLink(job)

	// Decode server-encoded i18n sentinels so titles + step labels follow the active locale.
	const localizedTitle = resolveI18nString(job.title ?? "", t) || `Job ${job.id.slice(0, 8)}`
	const localizedStep = resolveI18nString(job.currentStep ?? "", t)
	const localizedError = resolveI18nString(job.errorMessage ?? "", t)

	// Active pliego generation → link to tender-assistant session for live progress
	const activeLink = isActive && job.type === "pliego_generation"
		? (() => {
			// sessionId is in input (always available) or result (after expedient creation)
			const input = job.input as Record<string, unknown> | null
			const result = job.result as Record<string, unknown> | null
			const sessionId = (input?.sessionId ?? result?.sessionId) as string | undefined
			return sessionId ? `/tender-assistant/${sessionId}` : null
		})()
		: isActive && job.type === "specs_analysis"
			? specsAnalysisResumeLink(job)
			// Offer evaluation / comparison had no progress link at all (BUG-057).
			: isActive && (job.type === "evaluation" || job.type === "comparison")
				? (() => {
					const expedientId = (job.input as Record<string, unknown> | null)?.expedientId as string | undefined
					return expedientId ? `/expedients/${expedientId}?tab=evaluation` : null
				})()
				: null

	// Same destination as the page the user is already on: react-router would
	// no-op and the button read as broken. Hide it instead of firing nothing.
	const location = useLocation()
	const target = activeLink ? new URL(activeLink, window.location.origin) : null
	const currentParams = new URLSearchParams(location.search)
	const alreadyHere = Boolean(target
		&& target.pathname === location.pathname
		&& [...target.searchParams].every(([k, v]) => currentParams.get(k) === v))

	return (
		<div className="px-3 py-2.5 border-b border-border/40 last:border-b-0">
			<div className="flex items-start gap-2">
				<div className="mt-0.5 flex-shrink-0">
					<StatusIcon status={job.status} />
				</div>

				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-1.5">
						<span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
							{jobTypeLabel(job.type, t)}
						</span>
						<span className="text-[10px] text-muted-foreground">
							· {statusLabel(job.status, t)}
						</span>
					</div>
					<p className="text-sm font-medium truncate mt-0.5" title={localizedTitle}>
						{localizedTitle}
					</p>
				</div>

				<div className="flex-shrink-0 flex items-center gap-0.5">
					{isActive && (
						<Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onCancel(job.id)} title={t("backgroundJobs.cancel")}>
							<X className="h-3.5 w-3.5" />
						</Button>
					)}
					{isTerminal && (
						<Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onDismiss(job.id)} title={t("backgroundJobs.close")}>
							<X className="h-3.5 w-3.5" />
						</Button>
					)}
				</div>
			</div>

			{isActive && (
				<div className="mt-2 ml-6">
					<Progress value={job.progress || 0} className="h-1.5" />
					<div className="flex items-center justify-between mt-1">
						{localizedStep && (
							<p className="text-[11px] text-muted-foreground truncate flex-1">
								{localizedStep}
							</p>
						)}
						{activeLink && !alreadyHere && (
							<button
								onClick={() => router(activeLink)}
								className="text-[10px] text-blue-500 hover:text-blue-700 font-medium shrink-0 ml-2"
							>
								{t("backgroundJobs.viewProgress")}
							</button>
						)}
					</div>
				</div>
			)}

			{/* Action button when completed */}
			{job.status === "completed" && link && (
				<div className="mt-2 ml-6">
					<Button
						variant="outline"
						size="sm"
						className="h-7 text-xs gap-1.5"
						onClick={() => onNavigate(link, [job.id])}
					>
						<ExternalLink className="h-3 w-3" />
						{job.type === "comparison"
							? t("backgroundJobs.viewComparison")
							: job.type === "pliego_generation" || job.type === "document_review"
								? t("backgroundJobs.viewDocuments")
								: job.type === "specs_analysis"
									? ((job.result as Record<string, unknown> | null)?.createdExpedientId || (job.result as Record<string, unknown> | null)?.createdTemplateId
										? t("backgroundJobs.viewExpedient")
										: t("backgroundJobs.reviewAnalysis"))
									: t(labelKeyForLink(link))}
					</Button>
				</div>
			)}

			{/* Warning for completed import with no template created */}
			{job.status === "completed" && !link && job.type === "tender_import" && (
				<div className="mt-2 ml-6">
					<p className="text-xs text-amber-600">
						{t("backgroundJobs.noTemplateCreated")}
					</p>
				</div>
			)}

			{/* Warning for completed generation with failed sections */}
			{job.status === "completed" && job.type === "pliego_generation" && (() => {
				const failed = (job.result as { failedSections?: string[] } | null)?.failedSections
				return failed && failed.length > 0 ? (
					<div className="mt-2 ml-6">
						<p className="text-xs text-amber-600" title={failed.join(", ")}>
							{t("backgroundJobs.sectionsFailed", { count: failed.length })}
						</p>
					</div>
				) : null
			})()}

			{/* Warning for a generation whose criteria extraction produced nothing (BUG-048) */}
			{job.status === "completed" && job.type === "pliego_generation" && (() => {
				const issue = (job.result as { criteriaExtractionIssue?: string } | null)?.criteriaExtractionIssue
				if (!issue) return null
				// "dropped_N" means criteria WERE extracted but some rows were lost,
				// and "threw:" says nothing about the document — telling the user the
				// pliego has no criteria would be false in both cases.
				const readingIncomplete = issue.startsWith("dropped_") || issue.startsWith("threw:")
				return (
					<div className="mt-2 ml-6">
						<p className="text-xs text-amber-600" title={issue}>
							{t(readingIncomplete
								? "backgroundJobs.criteriaExtractionIncomplete"
								: "backgroundJobs.criteriaExtractionFailed")}
						</p>
					</div>
				)
			})()}

			{/* Error message + retry for failed/cancelled jobs */}
			{(job.status === "failed" || job.status === "cancelled") && (
				<div className="mt-2 ml-6">
					{localizedError && (
						<p className="text-xs text-red-500 truncate mb-1.5" title={localizedError}>
							{localizedError}
						</p>
					)}
					{canRetry && (
						<Button
							variant="outline"
							size="sm"
							className="h-7 text-xs gap-1.5"
							onClick={() => onRetry(job.id)}
							disabled={isRetrying}
						>
							{isRetrying ? (
								<Loader2 className="h-3 w-3 animate-spin" />
							) : (
								<RotateCcw className="h-3 w-3" />
							)}
							{isRetrying ? t("backgroundJobs.retrying") : t("backgroundJobs.retry")}
						</Button>
					)}
				</div>
			)}
		</div>
	)
}

// ── Aggregate group row ─────────────────────────────────────────────

function GroupRow({ group, onCancelGroup, onDismissGroup, onNavigate, onRetry, isRetrying, t }: {
	group: TemplateGroup
	onCancelGroup: (group: TemplateGroup) => void
	onDismissGroup: (group: TemplateGroup) => void
	onNavigate: (path: string, jobIds: string[]) => void
	onRetry: (id: string) => void
	isRetrying: boolean
	t: ReturnType<typeof useTranslations>
}) {
	const isActive = group.status === "processing"
	const link = `/procurement-templates/${group.targetId}`
	const name = resolveI18nString(group.entityTitle, t) || group.entityTitle
	const title = t("backgroundJobs.preparingTemplate", { name: name.slice(0, 80) })
	const failed = group.failedMember
	const failedName = failed ? (resolveI18nString(failed.title ?? "", t) || failed.title || "") : ""
	const failedError = failed ? resolveI18nString(failed.errorMessage ?? "", t) : ""

	return (
		<div className="px-3 py-2.5 border-b border-border/40 last:border-b-0">
			<div className="flex items-start gap-2">
				<div className="mt-0.5 flex-shrink-0">
					<StatusIcon status={group.status} />
				</div>

				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-1.5">
						<span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
							{t("backgroundJobs.templatePreparation")}
						</span>
						<span className="text-[10px] text-muted-foreground">
							· {statusLabel(group.status, t)}
						</span>
					</div>
					<p className="text-sm font-medium truncate mt-0.5" title={title}>
						{title}
					</p>
				</div>

				<div className="flex-shrink-0 flex items-center gap-0.5">
					{isActive ? (
						<Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onCancelGroup(group)} title={t("backgroundJobs.cancel")}>
							<X className="h-3.5 w-3.5" />
						</Button>
					) : (
						<Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onDismissGroup(group)} title={t("backgroundJobs.close")}>
							<X className="h-3.5 w-3.5" />
						</Button>
					)}
				</div>
			</div>

			{isActive && (
				<div className="mt-2 ml-6">
					<Progress value={group.progress} className="h-1.5" />
					<p className="text-[11px] text-muted-foreground mt-1">
						{t("backgroundJobs.docsProgress", { completed: group.completedDocs, total: group.totalDocs })}
					</p>
				</div>
			)}

			{group.status === "completed" && (
				<div className="mt-2 ml-6">
					<Button
						variant="outline"
						size="sm"
						className="h-7 text-xs gap-1.5"
						onClick={() => onNavigate(link, group.memberIds)}
					>
						<ExternalLink className="h-3 w-3" />
						{t("backgroundJobs.viewTemplate")}
					</Button>
				</div>
			)}

			{group.status === "failed" && failed && (
				<div className="mt-2 ml-6">
					<p className="text-xs text-red-500 truncate mb-1.5" title={failedError || failedName}>
						{t("backgroundJobs.documentFailed", { name: failedName })}
					</p>
					<Button
						variant="outline"
						size="sm"
						className="h-7 text-xs gap-1.5"
						onClick={() => onRetry(failed.id)}
						disabled={isRetrying}
					>
						{isRetrying ? (
							<Loader2 className="h-3 w-3 animate-spin" />
						) : (
							<RotateCcw className="h-3 w-3" />
						)}
						{isRetrying ? t("backgroundJobs.retrying") : t("backgroundJobs.retry")}
					</Button>
				</div>
			)}
		</div>
	)
}

// ── Main panel ──────────────────────────────────────────────────────

export function BackgroundJobsPanel() {
	const router = useNavigate()
	const t = useTranslations()
	// The tender-assistant chat composer sits at the bottom of the viewport; at
	// laptop widths the panel overlapped its send button and INTERCEPTED the
	// clicks (found via Playwright 23/08; Carlos also flagged the panel
	// "ocupando espacio vital"). Lift the panel above the composer there.
	const { pathname } = useLocation()
	const liftAboveComposer = pathname.startsWith("/tender-assistant")
	const { jobs, isMinimized, updateJob, removeJob, toggleMinimized, addJob, clearCompleted } =
		useBackgroundJobsStore()
	// Silent jobs stay in the store (detail views revalidate on their completion)
	// but never get a card.
	const displayItems = React.useMemo(() => buildDisplayItems(jobs.filter((job) => !isSilentJob(job))), [jobs])
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const initialLoadDone = useRef(false)
	const [retryingIds, setRetryingIds] = React.useState<Set<string>>(new Set())

	// ── Load active jobs from DB on mount (survives page refresh) ──
	useEffect(() => {
		if (initialLoadDone.current) return
		initialLoadDone.current = true

		getAuthHeaders().then((headers) => {
			if (!headers.Authorization) {
				initialLoadDone.current = false
				// Nothing was loaded, but consumers must not read the empty list as
				// "nothing is running" forever — the 10s discovery below re-marks it.
				return
			}
			return fetch("/api/jobs?status=pending,processing", { headers })
				.then((res) => res.ok ? res.json() : { success: false, data: [] })
				.then((data) => {
					if (data.success && Array.isArray(data.data)) {
						const existingIds = new Set(jobs.map((j) => j.id))
						for (const remoteJob of data.data) {
							if (!existingIds.has(remoteJob.id)) {
								addJob({
									id: remoteJob.id,
									type: remoteJob.type || "tender_import",
									status: remoteJob.status || "pending",
									progress: remoteJob.progress || 0,
									currentStep: remoteJob.currentStep || null,
									title: remoteJob.title || `Job ${remoteJob.id.slice(0, 8)}`,
									subtitle: remoteJob.subtitle || null,
									input: remoteJob.input || null,
									result: remoteJob.result || null,
									errorMessage: remoteJob.errorMessage || null,
									createdAt: remoteJob.createdAt || new Date().toISOString(),
									startedAt: remoteJob.startedAt || null,
									completedAt: remoteJob.completedAt || null,
								})
							}
						}
					}
				})
				.catch(() => {
					// Silently ignore — if API fails, we just don't restore jobs
				})
				.finally(() => {
					// The store's job list is only meaningful once this has answered:
					// until then an empty list means "not loaded", not "idle".
					useBackgroundJobsStore.getState().markJobsHydrated()
				})
		})
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// ── Periodically discover NEW server-side jobs (e.g. section extraction spawned by tender import) ──
	useEffect(() => {
		const interval = setInterval(async () => {
			const headers = await getAuthHeaders()
			if (!headers.Authorization) return
			fetch("/api/jobs?status=pending,processing", { headers })
				.then(r => r.ok ? r.json() : { success: false, data: [] })
				.then(data => {
					if (!data.success || !Array.isArray(data.data)) return
					const existingIds = new Set(jobs.map(j => j.id))
					for (const rj of data.data) {
						if (!existingIds.has(rj.id)) {
							addJob({
								id: rj.id, type: rj.type || "tender_import", status: rj.status || "pending",
								progress: rj.progress || 0, currentStep: rj.currentStep || null,
								title: rj.title || `Job ${rj.id.slice(0, 8)}`, subtitle: rj.subtitle || null,
								input: rj.input || null, result: rj.result || null, errorMessage: rj.errorMessage || null,
								createdAt: rj.createdAt || new Date().toISOString(), startedAt: rj.startedAt || null, completedAt: null,
							})
						}
					}
				})
				.catch(() => {})
				// Recovery path: if the mount-time load never ran (no token yet), this
				// is what eventually tells consumers the job list can be trusted.
				.finally(() => { useBackgroundJobsStore.getState().markJobsHydrated() })
		}, 10000) // Check every 10s
		return () => clearInterval(interval)
	}, [jobs, addJob])

	// ── Poll active jobs individually (recursive setTimeout — never overlaps) ───
	const isPollingRef = useRef(false)
	const hasActive = jobs.some((j) => j.status === "pending" || j.status === "processing")

	// The panel is pinned bottom-right above everything (z-50) and auto-expands on
	// every new job, so once a task FINISHES it just sits there covering whatever
	// button is in that corner — repeatedly hiding the control the user needs next.
	// Collapse it on its own a few seconds after there is nothing running — UNLESS a
	// completed card still offers a FRESH (< 60s old) result link (BUG-070): that
	// card is the "your evaluation is ready" notice, and collapsing the panel is
	// how users missed it. Older completed cards no longer block the collapse —
	// they are about to auto-dismiss anyway.
	const hasFreshNotice = hasFreshCompletedNotice(displayItems)
	useEffect(() => {
		if (hasActive || isMinimized || jobs.length === 0 || hasFreshNotice) return
		const timer = setTimeout(() => {
			// Re-check: a new job may have started or finished while we waited.
			const { jobs: current, isMinimized: minimized } = useBackgroundJobsStore.getState()
			const stillIdle = !current.some((j) => j.status === "pending" || j.status === "processing")
			const stillNoFreshNotice = !hasFreshCompletedNotice(buildDisplayItems(current))
			if (stillIdle && stillNoFreshNotice && !minimized) toggleMinimized()
		}, 8000)
		return () => clearTimeout(timer)
	}, [hasActive, isMinimized, jobs.length, hasFreshNotice, toggleMinimized])

	useEffect(() => {
		if (!hasActive) {
			// Stop polling
			if (pollRef.current) {
				clearTimeout(pollRef.current)
				pollRef.current = null
			}
			isPollingRef.current = false
			return
		}

		// Already polling — don't start another loop
		if (isPollingRef.current) return
		isPollingRef.current = true

		const poll = async () => {
			// Read current jobs from store (avoids stale closure)
			const currentJobs = useBackgroundJobsStore.getState().jobs
			const activeJobs = currentJobs.filter((j) => j.status === "pending" || j.status === "processing")
			if (activeJobs.length === 0) {
				isPollingRef.current = false
				return
			}

			await Promise.allSettled(
				activeJobs.map(async (job) => {
					try {
						const headers = await getAuthHeaders()
						const res = await fetch(`/api/jobs/${job.id}`, { headers })
						if (!res.ok) {
							if (res.status === 404) {
								updateJob(job.id, {
									status: "failed",
									progress: 0,
									currentStep: null,
									errorMessage: t("backgroundJobs.staleTimeout"),
									completedAt: new Date().toISOString(),
								})
							}
							return
						}
						const data = await res.json()
						if (!data.success || !data.data) return

						const remote = data.data
						updateJob(job.id, {
							status: remote.status,
							progress: remote.progress || 0,
							currentStep: remote.currentStep || null,
							input: remote.input || null,
							result: remote.result,
							errorMessage: remote.errorMessage || null,
							startedAt: remote.startedAt || null,
							completedAt: remote.completedAt || null,
						})

						// A job just finished with a result to show (BUG-070): pop the
						// panel open so the completion notice + link is actually seen,
						// even if the user minimized it while the job was running.
						// A job folded into an aggregate card only pops the panel once
						// its WHOLE group is done — that is when its card shows a link.
						if (remote.status === "completed") {
							const state = useBackgroundJobsStore.getState()
							const item = buildDisplayItems(state.jobs).find((it) =>
								it.kind === "job" ? it.job.id === job.id : it.group.memberIds.includes(job.id))
							const showsNotice = item !== undefined && (item.kind === "group"
								? item.group.status === "completed"
								: getJobLink(item.job) !== null)
							if (showsNotice && state.isMinimized) {
								state.toggleMinimized()
							}
						}
					} catch {
						// Network error — skip, retry next cycle
					}
				})
			)

			// Schedule next poll AFTER all responses complete (no request stacking)
			if (isPollingRef.current) {
				pollRef.current = setTimeout(poll, POLL_INTERVAL_MS)
			}
		}

		// Start first poll immediately
		poll()

		return () => {
			isPollingRef.current = false
			if (pollRef.current) {
				clearTimeout(pollRef.current)
				pollRef.current = null
			}
		}
	}, [hasActive, updateJob])

	// ── Auto-dismiss completed cards 60s after completion ──
	// Failed/cancelled cards stay until dismissed or cleared. Members of an
	// aggregate card only leave when the WHOLE group has been done for 60s,
	// so a half-finished group never loses its doc count mid-flight.
	useEffect(() => {
		const timer = setInterval(() => {
			const { jobs: current, removeJob: remove } = useBackgroundJobsStore.getState()
			if (current.length === 0) return
			const expired = (completedAt: string | null) =>
				completedAt !== null && Date.now() - new Date(completedAt).getTime() >= AUTO_DISMISS_MS
			for (const item of buildDisplayItems(current)) {
				if (item.kind === "job") {
					if (item.job.status === "completed" && expired(item.job.completedAt)) remove(item.job.id)
				} else if (item.group.status === "completed" && expired(item.group.completedAt)) {
					item.group.memberIds.forEach(remove)
				}
			}
		}, 5000)
		return () => clearInterval(timer)
	}, [])

	const handleCancel = useCallback(async (id: string) => {
		try {
			const headers = await getAuthHeaders()
			await fetch(`/api/jobs/${id}`, {
				method: "POST",
				headers,
				body: JSON.stringify({ action: "cancel" }),
			})
		} catch {
			// ignore
		}
	}, [])

	const handleDismiss = useCallback(async (id: string) => {
		removeJob(id)
	}, [removeJob])

	const handleRetry = useCallback(async (id: string) => {
		setRetryingIds((prev) => new Set(prev).add(id))
		try {
			const headers = await getAuthHeaders()
			const failedJob = jobs.find((j) => j.id === id)
			const res = await fetch(`/api/jobs/${id}`, {
				method: "POST",
				headers,
				body: JSON.stringify({ action: "retry" }),
			})
			const data = await res.json()
			if (data.success && data.data?.newJobId) {
				const newType = ((data.data.type as JobType | undefined) || failedJob?.type || "tender_import") as JobType
				const defaultTitle = newType === "tender_import"
					? t("backgroundJobs.importTenders", { count: data.data.totalTenders || 1 })
					: (failedJob?.title || `Job ${data.data.newJobId.slice(0, 8)}`)

				removeJob(id)
				addJob({
					id: data.data.newJobId,
					type: newType,
					status: "pending",
					progress: 0,
					currentStep: null,
					title: data.data.title || defaultTitle,
					subtitle: failedJob?.subtitle || null,
					input: null,
					result: null,
					errorMessage: null,
					createdAt: new Date().toISOString(),
					startedAt: null,
					completedAt: null,
				})
			} else if (data.error) {
				// Keep the failed job but surface the validation error
				const message = String(data.error)
				updateJob(id, { errorMessage: message })
			}
		} catch {
			// Network error — ignore
		} finally {
			setRetryingIds((prev) => {
				const next = new Set(prev)
				next.delete(id)
				return next
			})
		}
	}, [jobs, removeJob, addJob, updateJob, t])

	const handleCancelGroup = useCallback((group: TemplateGroup) => {
		group.activeMemberIds.forEach((id) => { void handleCancel(id) })
	}, [handleCancel])

	const handleDismissGroup = useCallback((group: TemplateGroup) => {
		group.memberIds.forEach((id) => { void handleDismiss(id) })
	}, [handleDismiss])

	const handleNavigate = useCallback((path: string, jobIds: string[]) => {
		jobIds.forEach((id) => { void handleDismiss(id) })
		router(path)

		// Extract highlight target from URL params
		const url = new URL(path, window.location.origin)
		const highlight = url.searchParams.get("highlight")
		if (!highlight) return

		// Scroll + highlight with retry loop (waits for page to render)
		let attempts = 0
		const tryScroll = () => {
			const el = document.getElementById(highlight)
			if (el) {
				el.scrollIntoView({ behavior: "smooth", block: "start" })
				el.classList.add("highlight-review")
				setTimeout(() => el.classList.remove("highlight-review"), 3000)
			} else if (attempts < 25) {
				attempts++
				setTimeout(tryScroll, 200)
			}
		}
		setTimeout(tryScroll, 1500)
	}, [handleDismiss, router])

	// Don't render if nothing is shown
	if (displayItems.length === 0) return null

	// Counts follow the CARDS shown, not the raw jobs — an aggregate of 6 jobs
	// reads as 1 task, matching what the user sees.
	const activeCount = displayItems.filter((item) =>
		item.kind === "group"
			? item.group.status === "processing"
			: item.job.status === "pending" || item.job.status === "processing",
	).length
	const totalCount = displayItems.length
	const hasTerminal = jobs.some((j) => j.status === "completed" || j.status === "failed" || j.status === "cancelled")

	return (
		<div className={`fixed ${liftAboveComposer ? "bottom-28" : "bottom-4"} right-20 z-50 w-80 max-h-[70vh] flex flex-col rounded-xl border border-border/60 bg-card shadow-lg overflow-hidden`}>
			{/* Header */}
			<div
				className="flex items-center justify-between px-3 py-2 border-b border-border/40 bg-muted/30 cursor-pointer select-none"
				onClick={toggleMinimized}
			>
				<div className="flex items-center gap-2">
					{activeCount > 0 && <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin" />}
					<span className="text-sm font-medium">
						{activeCount > 0
							? (activeCount === 1
								? t("backgroundJobs.activeJobsOne", { count: activeCount })
								: t("backgroundJobs.activeJobsOther", { count: activeCount }))
							: (totalCount === 1
								? t("backgroundJobs.totalJobsOne", { count: totalCount })
								: t("backgroundJobs.totalJobsOther", { count: totalCount }))
						}
					</span>
				</div>
				<div className="flex items-center gap-0.5">
					{hasTerminal && (
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6"
							title={t("backgroundJobs.clearCompleted")}
							onClick={(e) => {
								e.stopPropagation()
								clearCompleted()
							}}
						>
							<Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
						</Button>
					)}
					{isMinimized ? (
						<ChevronUp className="h-4 w-4 text-muted-foreground" />
					) : (
						<ChevronDown className="h-4 w-4 text-muted-foreground" />
					)}
				</div>
			</div>

			{/* Job list */}
			{!isMinimized && (
				<div className="flex-1 overflow-y-auto max-h-[60vh]">
					{displayItems.map((item) =>
						item.kind === "group" ? (
							<GroupRow
								key={`group:${item.group.targetId}`}
								group={item.group}
								onCancelGroup={handleCancelGroup}
								onDismissGroup={handleDismissGroup}
								onNavigate={handleNavigate}
								onRetry={handleRetry}
								isRetrying={item.group.failedMember !== null && retryingIds.has(item.group.failedMember.id)}
								t={t}
							/>
						) : (
							<JobRow
								key={item.job.id}
								job={item.job}
								onCancel={handleCancel}
								onDismiss={handleDismiss}
								onNavigate={handleNavigate}
								onRetry={handleRetry}
								isRetrying={retryingIds.has(item.job.id)}
								router={router}
								t={t}
							/>
						),
					)}
				</div>
			)}
		</div>
	)
}
